import React, { useState } from 'react';
import { trackAddToCart } from '../utils/metaPixel';
import { motion } from 'framer-motion';
import { XCircle, CheckCircle, Zap, Shield, Battery, Brain, Activity, Clock, ChevronRight, Flame } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { FAQItem } from '../components/FAQItem';
import { LegalModal } from '../components/LegalModal';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-21">
    {children}
  </motion.div>;
export function LandingPage() {
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-blue-500/30" data-id="element-22">
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden" data-id="element-23">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/20 blur-[120px] rounded-full pointer-events-none" data-id="element-24"></div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-25">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-26">
            <div className="inline-block mb-6 px-4 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-400 font-semibold tracking-wide text-sm uppercase" data-id="element-27">
              Para hombres mayores de 40 años
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-slate-50 leading-tight mb-6 tracking-tight" data-id="element-28">
              El Fin del "Momento del Sofá": <br className="hidden md:block" data-id="element-29" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300" data-id="element-30">
                Recupera la Energía de tus 20
              </span>
              <br data-id="element-31" />
              Después de los 40
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto font-medium leading-relaxed" data-id="element-32">
              El Protocolo de "Micro-Incentivos Metabólicos" para hombres de
              alto rendimiento que necesitan eliminar la neblina mental y la
              grasa abdominal sin pasar horas en el gimnasio.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="flex flex-col items-center" data-id="element-33">
            <img src="https://cdn.magicpatterns.com/uploads/ieuDAXn6gs4NWCF9oqqBzn/metabolismo402Bebookcover.png" alt="Metabolismo 40+ Reset Ebook" className="w-full max-w-2xl mx-auto drop-shadow-[0_20px_50px_rgba(59,130,246,0.2)] mb-10" data-id="element-34" />
            <div className="w-full max-w-md" data-id="element-35">
              <CTAButton onClick={scrollToOffer} data-id="element-36">
                <span data-id="element-37">Acceder al Reset Ahora</span>
                <ChevronRight className="w-6 h-6" data-id="element-38" />
              </CTAButton>
              <p className="text-center text-slate-500 mt-4 text-sm flex items-center justify-center gap-2" data-id="element-39">
                <Shield className="w-4 h-4" data-id="element-40" /> Acceso inmediato y seguro
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OPENING HOOK */}
      <section className="py-20 bg-slate-900 px-4 border-y border-slate-800/50" data-id="element-41">
        <div className="max-w-3xl mx-auto" data-id="element-42">
          <FadeIn data-id="element-43">
            <p className="text-2xl md:text-3xl font-bold text-slate-100 leading-relaxed mb-8 border-l-4 border-blue-500 pl-6" data-id="element-44">
              Son las 6:30 PM. Acabas de entrar por la puerta de casa. Estás
              agotado, irritado y lo único que puedes hacer es dejarte caer en
              el sofá mientras el mundo sigue girando a tu alrededor.
            </p>
            <p className="text-xl text-slate-300 leading-relaxed" data-id="element-45">
              Sientes que estás físicamente presente, pero mentalmente ausente
              para tu pareja y tus hijos. Te han dicho que "es la edad", que a
              los 40 el metabolismo simplemente se apaga.{' '}
              <strong className="text-red-400" data-id="element-46">Te han mentido.</strong>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-24 px-4" data-id="element-47">
        <div className="max-w-3xl mx-auto" data-id="element-48">
          <FadeIn data-id="element-49">
            <h2 className="text-3xl md:text-5xl font-black text-slate-50 mb-8" data-id="element-50">
              Tu cuerpo no está envejeciendo prematuramente; está atrapado en un{' '}
              <span className="text-blue-500" data-id="element-51">"Secuestro de Cortisol"</span>.
            </h2>
            <div className="space-y-6 text-lg md:text-xl text-slate-300 leading-relaxed" data-id="element-52">
              <p data-id="element-53">
                Años de estrés laboral, falta de sueño profundo y una
                alimentación que dispara tu insulina han hackeado tu sistema
                hormonal.
              </p>
              <p data-id="element-54">
                Tu testosterona está en declive porque tu cuerpo cree que estás
                en un estado de emergencia perpetuo. Por eso, cada caloría que
                comes se va directo a tu cintura y por eso necesitas tres cafés
                solo para sobrevivir a la reunión de la mañana.
              </p>
              <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-xl mt-8" data-id="element-55">
                <p className="text-red-200 font-semibold flex items-start gap-3" data-id="element-56">
                  <Flame className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-57" />
                  <span data-id="element-58">
                    El castigo físico (cardio extremo o dietas de hambre) solo
                    empeora el problema.
                  </span>
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-24 bg-slate-900 px-4" data-id="element-59">
        <div className="max-w-4xl mx-auto" data-id="element-60">
          <FadeIn data-id="element-61">
            <h2 className="text-3xl md:text-4xl font-black text-center text-slate-50 mb-16" data-id="element-62">
              Por qué lo que has intentado{' '}
              <span className="text-red-500" data-id="element-63">ha fallado</span>
            </h2>
            <div className="space-y-6" data-id="element-64">
              {['Las dietas Keto destruyen la producción de testosterona en hombres mayores de 40 años que tienen vidas estresantes.', 'El cardio de larga duración eleva aún más el cortisol, bloqueando la quema de grasa abdominal.', 'El ayuno intermitente extremo estresa tus glándulas suprarrenales, provocando más fatiga por la tarde.', "Los suplementos 'quemadores de grasa' solo te dan taquicardia sin atacar la raíz del problema hormonal.", "Intentar 'comer menos y moverse más' ignora que tu software metabólico está descalibrado."].map((item, i) => <div key={i} className="flex items-start gap-4 bg-slate-950/50 p-6 rounded-xl border border-slate-800" data-id="element-65">
                  <XCircle className="w-8 h-8 text-red-500 flex-shrink-0" data-id="element-66" />
                  <p className="text-lg text-slate-300" data-id="element-67">{item}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 px-4 relative overflow-hidden" data-id="element-68">
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-blue-600/10 blur-[150px] rounded-full pointer-events-none translate-x-1/2 -translate-y-1/2" data-id="element-69"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-70">
          <FadeIn data-id="element-71">
            <div className="text-center mb-12" data-id="element-72">
              <Zap className="w-16 h-16 text-blue-500 mx-auto mb-6" data-id="element-73" />
              <h2 className="text-3xl md:text-5xl font-black text-slate-50 mb-6" data-id="element-74">
                La solución no es el castigo, es el <br data-id="element-75" />
                <span className="text-blue-400" data-id="element-76">
                  Micro-Incentivo Metabólico (MIM)
                </span>
              </h2>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 p-8 md:p-12 rounded-2xl shadow-2xl backdrop-blur-sm" data-id="element-77">
              <p className="text-xl leading-relaxed text-slate-200 mb-6" data-id="element-78">
                A diferencia del ejercicio tradicional, los MIM son
                intervenciones de <strong data-id="element-79">menos de 5 minutos</strong> diseñadas
                para enviar una señal química específica a tus mitocondrias.
              </p>
              <p className="text-xl leading-relaxed text-slate-300" data-id="element-80">
                Al alternar choques térmicos controlados, movimientos
                multiplanares rápidos y un orden estratégico de nutrientes,
                obligas a tu cuerpo a salir del modo "supervivencia" y entrar en
                modo "rendimiento". Es como actualizar el software de un
                ordenador viejo para que vuelva a correr a máxima velocidad.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 bg-slate-900 px-4" data-id="element-81">
        <div className="max-w-5xl mx-auto" data-id="element-82">
          <FadeIn data-id="element-83">
            <h2 className="text-3xl md:text-5xl font-black text-center text-slate-50 mb-16" data-id="element-84">
              Lo que obtienes dentro del{' '}
              <span className="text-blue-500" data-id="element-85">Reset</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-86">
              {[{
              title: 'El Manual Principal',
              desc: 'La guía paso a paso para ejecutar el Reset de 24 horas.',
              icon: <Brain className="w-8 h-8 text-blue-400" data-id="element-87" />
            }, {
              title: 'Protocolo Despertar 3-2-1',
              desc: 'La rutina de 6 minutos para encender tu foco mental cada mañana.',
              icon: <Zap className="w-8 h-8 text-blue-400" data-id="element-88" />
            }, {
              title: 'La Matriz de Almuerzo Estratégico',
              desc: 'Cómo comer fuera de casa sin sufrir el colapso de las 3 PM.',
              icon: <Activity className="w-8 h-8 text-blue-400" data-id="element-89" />
            }, {
              title: 'El Escudo Nocturno',
              desc: 'La técnica de enfriamiento térmico para duplicar tu sueño profundo.',
              icon: <Shield className="w-8 h-8 text-blue-400" data-id="element-90" />
            }, {
              title: 'Checklist de Energía Diaria',
              desc: 'Una hoja de ruta visual para que nunca adivines qué hacer.',
              icon: <CheckCircle className="w-8 h-8 text-blue-400" data-id="element-91" />
            }, {
              title: 'Guía de Suplementación Crítica',
              desc: 'Los 3 minerales esenciales que el hombre de 40+ realmente necesita.',
              icon: <Battery className="w-8 h-8 text-blue-400" data-id="element-92" />
            }].map((item, i) => <div key={i} className="bg-slate-950 p-8 rounded-2xl border border-slate-800 hover:border-blue-500/50 transition-colors group" data-id="element-93">
                  <div className="mb-4 bg-slate-900 w-16 h-16 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-94">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-100 mb-2" data-id="element-95">
                    {item.title}
                  </h3>
                  <p className="text-slate-400" data-id="element-96">{item.desc}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-24 px-4" data-id="element-97">
        <div className="max-w-5xl mx-auto" data-id="element-98">
          <FadeIn data-id="element-99">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-100">
              {/* FOR */}
              <div className="bg-slate-900/50 border border-emerald-500/20 rounded-2xl p-8 md:p-10" data-id="element-101">
                <h3 className="text-2xl font-black text-emerald-400 mb-8 flex items-center gap-3" data-id="element-102">
                  <CheckCircle className="w-8 h-8" data-id="element-103" /> ESTO ES PARA TI SI:
                </h3>
                <ul className="space-y-6" data-id="element-104">
                  {['Hombres mayores de 40 años con responsabilidades profesionales y familiares.', 'Profesionales que sufren de neblina mental y falta de concentración por las tardes.', "Hombres que han ganado grasa abdominal a pesar de 'comer sano'.", 'Padres que quieren tener energía real para jugar con sus hijos al final del día.', 'Líderes que saben que su rendimiento físico es la base de su éxito profesional.'].map((text, i) => <li key={i} className="flex items-start gap-4" data-id="element-105">
                      <div className="mt-1 w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" data-id="element-106"></div>
                      <span className="text-slate-300" data-id="element-107">{text}</span>
                    </li>)}
                </ul>
              </div>

              {/* NOT FOR */}
              <div className="bg-slate-900/50 border border-red-500/20 rounded-2xl p-8 md:p-10" data-id="element-108">
                <h3 className="text-2xl font-black text-red-400 mb-8 flex items-center gap-3" data-id="element-109">
                  <XCircle className="w-8 h-8" data-id="element-110" /> ESTO NO ES PARA TI SI:
                </h3>
                <ul className="space-y-6" data-id="element-111">
                  {["Personas que buscan una 'pastilla mágica' sin cambiar un solo hábito.", 'Atletas de élite que ya tienen un equipo de biohackers a su disposición.', "Hombres que se conforman con sentirse 'cansados por la edad'."].map((text, i) => <li key={i} className="flex items-start gap-4" data-id="element-112">
                      <div className="mt-1 w-2 h-2 rounded-full bg-red-500 flex-shrink-0" data-id="element-113"></div>
                      <span className="text-slate-300" data-id="element-114">{text}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-slate-900 px-4" data-id="element-115">
        <div className="max-w-4xl mx-auto" data-id="element-116">
          <FadeIn data-id="element-117">
            <h2 className="text-3xl md:text-5xl font-black text-center text-slate-50 mb-16" data-id="element-118">
              Cómo Funciona el <span className="text-blue-500" data-id="element-119">Sistema</span>
            </h2>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-blue-500/50 before:to-transparent" data-id="element-120">
              {[{
              step: '1',
              title: 'Desactiva',
              desc: 'Aplica el Reset de Cortisol por la mañana para liberar el freno de mano de tus hormonas.'
            }, {
              step: '2',
              title: 'Incentiva',
              desc: 'Usa los MIM de 5 minutos para reactivar la quema de grasa parda y la claridad mental.'
            }, {
              step: '3',
              title: 'Blinda',
              desc: 'Instala el Escudo Nocturno para que tu cuerpo se repare mientras duermes, no solo descanse.'
            }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-121">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-slate-900 bg-blue-600 text-white font-bold text-xl shadow-[0_0_20px_rgba(59,130,246,0.5)] shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-122">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-slate-950 p-6 rounded-2xl border border-slate-800 shadow-xl" data-id="element-123">
                    <h3 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-124">
                      {item.title}
                    </h3>
                    <p className="text-slate-400" data-id="element-125">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-24 px-4 bg-blue-900/10 border-y border-blue-900/30" data-id="element-126">
        <div className="max-w-3xl mx-auto text-center" data-id="element-127">
          <FadeIn data-id="element-128">
            <Clock className="w-16 h-16 text-blue-400 mx-auto mb-8" data-id="element-129" />
            <p className="text-2xl md:text-3xl font-medium text-slate-200 leading-relaxed italic" data-id="element-130">
              "En los primeros 7 días, notarás que la alarma del despertador ya
              no es tu enemiga. La pesadez en las piernas desaparece y, por
              primera vez en años, llegarás a las 7 PM con la mente despejada y
              lista para lo que decidas hacer, no para lo que el cansancio te
              obligue a hacer."
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4" data-id="element-131">
        <div className="max-w-3xl mx-auto" data-id="element-132">
          <FadeIn data-id="element-133">
            <h2 className="text-3xl md:text-4xl font-black text-center text-slate-50 mb-12" data-id="element-134">
              Preguntas Frecuentes
            </h2>
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 md:p-8" data-id="element-135">
              <FAQItem question="¿Necesito equipo de gimnasio?" answer="No, los micro-incentivos se hacen con tu propio cuerpo en cualquier lugar." data-id="element-136" />
              <FAQItem question="¿Tengo que dejar de comer lo que me gusta?" answer="No, aprenderás a usar el orden de los alimentos para que el impacto metabólico sea mínimo." data-id="element-137" />
              <FAQItem question="¿Cuánto tiempo al día requiere?" answer="Menos de 15 minutos totales divididos en pequeñas cápsulas de tiempo." data-id="element-138" />
              <FAQItem question="¿Es seguro si tengo lesiones de espalda?" answer="Sí, el enfoque es de bajo impacto y alta respuesta hormonal." data-id="element-139" />
              <FAQItem question="¿Cuándo veré resultados?" answer="La energía mejora en 48-72 horas; los cambios físicos comienzan a los 14 días." data-id="element-140" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK */}
      <section id="offer-section" className="py-24 px-4 bg-slate-900 relative overflow-hidden" data-id="element-141">
        {/* Background glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[300px] bg-blue-600/10 blur-[100px] rounded-full pointer-events-none" data-id="element-142"></div>

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-143">
          <FadeIn data-id="element-144">
            <div className="bg-slate-950 border-2 border-blue-500/30 rounded-3xl p-8 md:p-12 text-center shadow-2xl relative overflow-hidden" data-id="element-145">
              {/* Highlight banner */}
              <div className="absolute top-0 inset-x-0 h-2 bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600" data-id="element-146"></div>

              <h2 className="text-3xl md:text-4xl font-black text-slate-50 mb-6" data-id="element-147">
                Recupera el mando de tu biología hoy mismo.
              </h2>
              <p className="text-xl text-slate-400 mb-10" data-id="element-148">
                El acceso completo a{' '}
                <strong className="text-slate-200" data-id="element-149">
                  Metabolismo 40+ Reset
                </strong>{' '}
                está disponible por una fracción de lo que cuesta una sesión con
                un endocrino o un entrenador personal.
              </p>

              <div className="mb-10" data-id="element-150">
                <p className="text-sm text-slate-500 uppercase tracking-widest font-bold mb-2" data-id="element-151">
                  Inversión Única
                </p>
                <div className="flex justify-center items-start gap-1" data-id="element-152">
                  <span className="text-3xl font-bold text-blue-500 mt-2" data-id="element-153">
                    $
                  </span>
                  <span className="text-7xl md:text-8xl font-black text-white tracking-tighter" data-id="element-154">
                    6.39
                  </span>
                </div>
              </div>

              <div className="max-w-md mx-auto" data-id="element-155">
                <CTAButton onClick={() => {
                trackAddToCart();
                window.open('https://thealphamethods.com/cart/44192449364054:1', '_blank');
              }} data-id="element-156">
                  <span data-id="element-157">Acceder al Reset Ahora</span>
                  <ChevronRight className="w-6 h-6" data-id="element-158" />
                </CTAButton>
                <p className="text-slate-500 mt-6 text-sm italic" data-id="element-159">
                  *Recibe el PDF al instante y comienza a recuperar tu energía
                  hoy mismo.*
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 border-t border-slate-800 bg-slate-950" data-id="element-160">
        <div className="flex flex-col items-center gap-3" data-id="element-161">
          <div className="flex items-center gap-4 text-sm" data-id="element-162">
            <button onClick={() => setLegalModal('privacy')} className="text-slate-500 hover:text-blue-400 transition-colors underline underline-offset-2" data-id="element-163">
              Política de Privacidad
            </button>
            <span className="text-slate-700" data-id="element-164">|</span>
            <button onClick={() => setLegalModal('terms')} className="text-slate-500 hover:text-blue-400 transition-colors underline underline-offset-2" data-id="element-165">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-slate-600 text-sm" data-id="element-166">
            © {new Date().getFullYear()} Metabolismo 40+ Reset. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <LegalModal isOpen={legalModal === 'privacy'} onClose={() => setLegalModal(null)} title="Política de Privacidad" data-id="element-167">
        <p className="text-slate-200 font-semibold" data-id="element-168">
          Última actualización: Marzo 2026
        </p>
        <p data-id="element-169">
          En Metabolismo 40+ Reset, nos comprometemos a proteger tu privacidad.
          Esta política describe cómo recopilamos, usamos y protegemos tu
          información personal.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-170">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-171">
          Recopilamos únicamente la información necesaria para procesar tu
          compra y entregarte el producto digital, incluyendo: nombre, dirección
          de correo electrónico y datos de pago procesados de forma segura a
          través de nuestro proveedor de pagos.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-172">
          2. Uso de la Información
        </h3>
        <p data-id="element-173">
          Tu información se utiliza exclusivamente para: procesar y entregar tu
          compra, enviarte el producto digital adquirido, y comunicarnos contigo
          respecto a tu pedido. No vendemos, alquilamos ni compartimos tu
          información personal con terceros con fines de marketing.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-174">
          3. Protección de Datos
        </h3>
        <p data-id="element-175">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado,
          alteración, divulgación o destrucción.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-176">4. Cookies</h3>
        <p data-id="element-177">
          Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
          navegación y analizar el tráfico del sitio. Puedes configurar tu
          navegador para rechazar cookies, aunque esto podría afectar la
          funcionalidad del sitio.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-178">
          5. Tus Derechos
        </h3>
        <p data-id="element-179">
          Tienes derecho a acceder, rectificar o eliminar tu información
          personal en cualquier momento. Para ejercer estos derechos, puedes
          contactarnos a través de los canales indicados en nuestro sitio.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-180">
          6. Cambios en esta Política
        </h3>
        <p data-id="element-181">
          Nos reservamos el derecho de actualizar esta política de privacidad en
          cualquier momento. Los cambios serán publicados en esta página con la
          fecha de actualización correspondiente.
        </p>
      </LegalModal>

      <LegalModal isOpen={legalModal === 'terms'} onClose={() => setLegalModal(null)} title="Términos y Condiciones" data-id="element-182">
        <p className="text-slate-200 font-semibold" data-id="element-183">
          Última actualización: Marzo 2026
        </p>
        <p data-id="element-184">
          Al acceder y utilizar este sitio web y adquirir nuestros productos
          digitales, aceptas los siguientes términos y condiciones.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-185">
          1. Producto Digital
        </h3>
        <p data-id="element-186">
          Metabolismo 40+ Reset es un producto de información digital en formato
          PDF. El contenido es educativo y no sustituye el consejo médico
          profesional. Consulta siempre a un profesional de la salud antes de
          realizar cambios en tu dieta o rutina de ejercicios.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-187">
          2. Entrega del Producto
        </h3>
        <p data-id="element-188">
          Una vez completado el pago, recibirás acceso inmediato al producto
          digital a través de un enlace de descarga enviado a tu correo
          electrónico. Es tu responsabilidad proporcionar una dirección de
          correo electrónico válida.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-189">
          3. Propiedad Intelectual
        </h3>
        <p data-id="element-190">
          Todo el contenido, incluyendo textos, gráficos, imágenes y materiales
          descargables, está protegido por derechos de autor. Queda prohibida la
          reproducción, distribución o reventa del material sin autorización
          expresa por escrito.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-191">
          4. Política de Reembolso
        </h3>
        <p data-id="element-192">
          Debido a la naturaleza digital del producto, no se ofrecen reembolsos
          una vez que el producto ha sido descargado o el enlace de acceso ha
          sido enviado. Te recomendamos revisar toda la información disponible
          antes de realizar tu compra.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-193">
          5. Limitación de Responsabilidad
        </h3>
        <p data-id="element-194">
          Los resultados pueden variar de persona a persona. No garantizamos
          resultados específicos. El contenido se proporciona "tal cual" y no
          nos hacemos responsables de decisiones tomadas basándose en la
          información proporcionada.
        </p>
        <h3 className="text-slate-100 font-bold text-base mt-4" data-id="element-195">
          6. Modificaciones
        </h3>
        <p data-id="element-196">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. El uso continuado del sitio después de cualquier cambio
          constituye la aceptación de los nuevos términos.
        </p>
      </LegalModal>
    </div>;
}